import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import { OidcIdentityContext } from "@rsmus/react-auth";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import { useRecoilValue } from "recoil";

import { userAssignmentsState } from "../../../App";
import { useSnackbar } from "../../../hooks";
import { REACT_APP_SYSTEM_ADMIN_USERS } from "../../../envVariables";
import {
  ConsolidatedPowerBiReportDTO,
  PowerBIReport,
} from "../../../api-client-nswag/taxportal-client";
import { taxPortalClientApi, userAssignmentClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import AccessDenied from "../../pageNotFound/AccessDenied";

import "./CustomPowerBISetup.scss";

type CustomPowerBIReportRecord = {
  id: string;
  reportType: number;
  powerBIName: string;
  roleAccess: string[];
  siteId: string;
};

type EmbedParamMode = "reportType" | "biReport";

type ParsedEmbedRouteParam = {
  value: number;
  mode: EmbedParamMode;
} | null;

const parseEmbedRouteParam = (rawValue?: string): ParsedEmbedRouteParam => {
  const raw = rawValue ? decodeURIComponent(rawValue).trim() : "";
  if (!raw) return null;

  // Explicit reportType mode: rt-65, reportType:65
  let match = raw.match(/^(?:rt|reporttype)[:\-_](\d+)$/i);
  if (match) {
    return { value: parseInt(match[1], 10), mode: "reportType" };
  }

  // Explicit biReport enum mode: br-115, biReport:115, enum:115
  match = raw.match(/^(?:br|bireport|enum|syncenum)[:\-_](\d+)$/i);
  if (match) {
    return { value: parseInt(match[1], 10), mode: "biReport" };
  }

  // Backward-compatible default: plain number => reportType
  if (/^\d+$/.test(raw)) {
    return { value: parseInt(raw, 10), mode: "reportType" };
  }

  return null;
};

const getSiteIdFromSessionStorage = (): string | null => {
  const existing = sessionStorage.getItem("siteId") ?? "";
  let parsed = parseInt(existing, 10);
  if (parsed && !Number.isNaN(parsed)) return String(parsed);

  const ref = document.referrer ?? "";
  const refSite = ref.split("siteID=")[1]?.split("&")[0];
  if (refSite) {
    parsed = parseInt(refSite, 10);
    if (parsed && !Number.isNaN(parsed)) {
      sessionStorage.setItem("siteId", String(parsed));
      return String(parsed);
    }
  }

  const href = document.location?.href ?? "";
  if (href.includes("siteID=")) {
    try {
      const urlParams = new URL(href);
      const params = new URLSearchParams(urlParams.search);
      const site = params.get("siteID") ?? "";
      parsed = parseInt(site, 10);
      if (parsed && !Number.isNaN(parsed)) {
        sessionStorage.setItem("siteId", String(parsed));
        return String(parsed);
      }
    } catch {
      // ignore
    }
  }

  const raw = sessionStorage.getItem("siteId") ?? "";
  parsed = parseInt(raw, 10);
  if (!parsed || Number.isNaN(parsed)) return null;
  return String(parsed);
};

const normalizeRole = (role: string) =>
  String(role ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const isSiteAdminRole = (role: string) => normalizeRole(role).includes("siteadmin");

export const CustomPowerBISiteReports: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const routeSiteId = searchParams.get("siteID");
  const routeReportName = searchParams.get("PowerBIReport") ?? undefined;
  const { showSnackbar } = useSnackbar();
  const { user } = useContext(OidcIdentityContext);
  const userAssignments = useRecoilValue(userAssignmentsState);
  const systemAdminUsers = useMemo<string[]>(() => {
    const raw = REACT_APP_SYSTEM_ADMIN_USERS;
    if (!raw) return [];
    return raw.split(",").map((e: string) => e.replace(/"/g, "").trim().toLowerCase()).filter(Boolean);
  }, []);

  const [fallbackAssignments, setFallbackAssignments] = useState<any[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const assignmentsRequestKeyRef = useRef<string>("");
  const reportsRequestKeyRef = useRef<string>("");
  const embedRequestKeyRef = useRef<string>("");
  const showSnackbarRef = useRef(showSnackbar);

  const siteId = useMemo(() => {
    const fromRoute = parseInt(routeSiteId ?? "", 10);
    if (fromRoute && !Number.isNaN(fromRoute)) return fromRoute;

    const fromSession = parseInt(getSiteIdFromSessionStorage() ?? "", 10);
    if (fromSession && !Number.isNaN(fromSession)) return fromSession;

    return 0;
  }, [routeSiteId]);

  const directEmbedParam = useMemo(
    () => parseEmbedRouteParam(routeReportName),
    [routeReportName],
  );

  const directReportTypeId = directEmbedParam?.mode === "reportType" ? directEmbedParam.value : null;

  const hideDropdown = directEmbedParam !== null;

  const username = user?.profile?.preferred_username ?? "";
  const isSystemAdmin = systemAdminUsers.includes(username.toLowerCase());

  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  useEffect(() => {
    if (siteId) {
      sessionStorage.setItem("siteId", String(siteId));
    }
  }, [siteId]);

  useEffect(() => {
    let cancelled = false;
    const requestKey = siteId && username ? `${siteId}:${username}` : "";

    if (
      isSystemAdmin ||
      !siteId ||
      !username ||
      (userAssignments?.length ?? 0) > 0 ||
      assignmentsRequestKeyRef.current === requestKey
    ) {
      return;
    }

    assignmentsRequestKeyRef.current = requestKey;

    (async () => {
      setAssignmentsLoading(true);
      try {
        const response = await userAssignmentClientApi.userAssignment_GetUserAssignmentList(
          siteId,
          username,
          null,
          null,
        );
        const sites = (response as any)?.data?.data ?? [];
        const assignmentList: any[] = Array.isArray(sites) ? sites : [];
        if (!cancelled) {
          setFallbackAssignments(assignmentList);
          if (assignmentList.length === 0) {
            setIsAccessDenied(true);
          } else {
            const siteIds = assignmentList.map((a: any) => a.hqSiteId).filter(Boolean);
            if (!siteIds.includes(siteId)) setIsAccessDenied(true);
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          showSnackbarRef.current({
            message: error?.message ?? "Failed to load site access for this page.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setAssignmentsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteId, username, userAssignments, isSystemAdmin]);

  const effectiveAssignments = (userAssignments?.length ?? 0) > 0 ? userAssignments : fallbackAssignments;

  useEffect(() => {
    if (isSystemAdmin || (userAssignments?.length ?? 0) === 0 || !siteId) return;
    const siteIds = (userAssignments ?? []).map((a) => a.hqSiteId).filter(Boolean);
    if (!siteIds.includes(siteId)) setIsAccessDenied(true);
  }, [isSystemAdmin, userAssignments, siteId]);

  const roles = useMemo(() => {
    const items: string[] = [];

    for (const assignment of effectiveAssignments ?? []) {
      if (assignment.roleName) items.push(assignment.roleName);

      const groups = assignment.userGroups
        ? assignment.userGroups
            .split(",")
            .map((group: string) => group.trim())
            .filter(Boolean)
        : [];

      items.push(...groups);
    }

    return items;
  }, [effectiveAssignments]);

  const normalizedUserRoles = useMemo(
    () => roles.map(normalizeRole).filter(Boolean),
    [roles],
  );

  const hasSiteAdminAccess = useMemo(
    () => roles.some(isSiteAdminRole),
    [roles],
  );

  const userSiteIds = useMemo(
    () =>
      Array.from(
        new Set(
          (effectiveAssignments ?? [])
            .map((assignment) => assignment.hqSiteId)
            .filter((id): id is number => id != null && id !== undefined),
        ),
      ),
    [effectiveAssignments],
  );

  const [recordsLoading, setRecordsLoading] = useState(false);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [records, setRecords] = useState<CustomPowerBIReportRecord[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string>("");
  const [embeddedReportConfig, setEmbeddedReportConfig] = useState<models.IReportEmbedConfiguration>({
    type: "report",
    embedUrl: "",
    tokenType: models.TokenType.Embed,
    accessToken: "",
    settings: undefined,
  });

  const accessibleRecords = useMemo(() => {
    if (hasSiteAdminAccess) return records;

    return records.filter((record) => {
      const allowedRoles = (record.roleAccess ?? []).map(normalizeRole).filter(Boolean);
      // Empty roleAccess means no per-role restriction — all authenticated users can access
      if (allowedRoles.length === 0) return true;

      return allowedRoles.some((role) => normalizedUserRoles.includes(role));
    });
  }, [hasSiteAdminAccess, normalizedUserRoles, records]);

  const selectedRecord = useMemo(
    () => accessibleRecords.find((record) => record.id === selectedReportId) ?? null,
    [accessibleRecords, selectedReportId],
  );

  const embedReportTypeId = useMemo(() => {
    if (directEmbedParam !== null) return directEmbedParam.value;
    if (!selectedRecord) return null;

    const parsed = parseInt(selectedRecord.id, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }, [directEmbedParam, selectedRecord]);

  useEffect(() => {
    let cancelled = false;
    const reportsRequestKey = `${siteId}:${userSiteIds.join(",")}:${hasSiteAdminAccess}`;

    if (!siteId) {
      showSnackbarRef.current({ message: "Site ID is missing from the route.", severity: "error" });
      return;
    }

    if (assignmentsLoading) {
      return;
    }

    if (!isSystemAdmin && (effectiveAssignments?.length ?? 0) > 0 && !userSiteIds.includes(siteId)) {
      showSnackbarRef.current({ message: "You do not have access to this site.", severity: "error" });
      return;
    }

    if (reportsRequestKeyRef.current === reportsRequestKey) {
      return;
    }

    reportsRequestKeyRef.current = reportsRequestKey;

    (async () => {
      setRecordsLoading(true);
      try {
        const response: any = await taxPortalClientApi.reports_GetConsolidatedPowerBiReports(siteId, roles);
        const list: ConsolidatedPowerBiReportDTO[] = Array.isArray(response) ? response : [];

        const mapped = list.map((dto: ConsolidatedPowerBiReportDTO): CustomPowerBIReportRecord => ({
          id: String(dto.reportType),
          reportType: dto.reportType,
          powerBIName: dto.reportName || dto.reportTypeName || "Unnamed Report",
          roleAccess: [], // Consolidated reports don't have per-role access restrictions
          siteId: String(dto.hqSiteId ?? siteId),
        }));

        if (!cancelled) setRecords(mapped);
      } catch (error: any) {
        if (!cancelled) {
          showSnackbarRef.current({
            message: error?.message ?? "Failed to load custom Power BI reports.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setRecordsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [assignmentsLoading, effectiveAssignments, hasSiteAdminAccess, isSystemAdmin, roles, siteId, userSiteIds]);

  useEffect(() => {
    if (!accessibleRecords.length) {
      // Don't clear when a URL report name is present — records may still be loading
      if (!routeReportName) {
        setSelectedReportId("");
      }
      return;
    }

    if (directReportTypeId !== null) {
      const match = accessibleRecords.find(
        (record) => record.reportType === directReportTypeId || record.id === String(directReportTypeId),
      );

      if (match) {
        setSelectedReportId(match.id);
        return;
      }

      setSelectedReportId("");
      return;
    }

    if (routeReportName) {
      const normalize = (s: string) =>
        s.trim().toLowerCase().replace(/[\s_-]+/g, " ");
      const decodedName = normalize(decodeURIComponent(routeReportName));
      const match = accessibleRecords.find(
        (record) => normalize(record.powerBIName) === decodedName,
      );
      if (match) {
        setSelectedReportId(match.id);
        return;
      }
    }

    if (!accessibleRecords.some((record) => record.id === selectedReportId)) {
      setSelectedReportId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleRecords, directReportTypeId, routeReportName]);

  useEffect(() => {
    let cancelled = false;

    if (embedReportTypeId === null) {
      embedRequestKeyRef.current = "";
      setEmbeddedReportConfig({
        type: "report",
        embedUrl: "",
        tokenType: models.TokenType.Embed,
        accessToken: "",
        settings: undefined,
      });
      return;
    }

    const embedRequestKey = `${siteId}:${directEmbedParam?.mode ?? "auto"}:${directEmbedParam?.value ?? embedReportTypeId}`;
    if (embedRequestKeyRef.current === embedRequestKey) {
      return;
    }

    embedRequestKeyRef.current = embedRequestKey;

    (async () => {
      setEmbedLoading(true);
      try {
        let embedded;

        if (directEmbedParam !== null) {
          embedded = await taxPortalClientApi.reports_GetConsolidatedPowerBiEmbedUrl(
            undefined,
            embedReportTypeId as PowerBIReport,
          );
        } else {
          embedded = await taxPortalClientApi.reports_GetConsolidatedPowerBiEmbedUrl(
            embedReportTypeId,
            undefined,
          );
        }

        const embedUrl = String(embedded?.embedUrl ?? "").trim();
        const accessToken = String(embedded?.token ?? "").trim();

        if (!embedUrl || !accessToken) {
          throw new Error("No report URL or access token available.");
        }

        if (!cancelled) {
          setEmbeddedReportConfig({
            type: "report",
            embedUrl,
            tokenType: models.TokenType.Embed,
            accessToken,
            settings: {
              filterPaneEnabled: false,
              navContentPaneEnabled: false,
            },
          });
        }
      } catch (error: any) {
        if (!cancelled) {
          setEmbeddedReportConfig({
            type: "report",
            embedUrl: "",
            tokenType: models.TokenType.Embed,
            accessToken: "",
            settings: undefined,
          });
          showSnackbarRef.current({
            message: error?.message ?? "Failed to load embedded report URL.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setEmbedLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [directEmbedParam, embedReportTypeId, siteId]);
  
  if (!isSystemAdmin && isAccessDenied) return <AccessDenied />;

  return (
    <div className="custom-pbi-setup custom-pbi-site-reports" style={hideDropdown ? { margin: 0 } : undefined}>
      {!hideDropdown && (
        <div className="custom-pbi-site-reports__toolbar">
          <TextField
            select
            className="custom-pbi-site-reports__dropdown"
            value={selectedReportId}
            onChange={(event) => setSelectedReportId(String(event.target.value ?? ""))}
            size="small"
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  className: "custom-pbi-site-reports__menuPaper",
                },
              },
            }}
            disabled={recordsLoading || !accessibleRecords.length}
          >
            <MenuItem value="" disabled>
              Select Report
            </MenuItem>
            {accessibleRecords.map((record) => (
              <MenuItem key={record.id} value={record.id}>
                {record.powerBIName}
              </MenuItem>
            ))}
          </TextField>
        </div>
      )}

      {assignmentsLoading || recordsLoading ? (
        <div className="custom-pbi-setup__loadingState">Loading Power BI reports...</div>
      ) : !accessibleRecords.length && !hideDropdown ? (
        <div className="custom-pbi-site-reports__emptyState">
          No custom Power BI reports are available for your access on this site.
        </div>
      ) : embedLoading ? (
        <div className="custom-pbi-setup__loadingState">Loading Power BI report...</div>
      ) : embeddedReportConfig.embedUrl ? (
        <div className="custom-pbi-setup__embeddedReportView" style={{ width: "100%", height: "100%" }}>
          <div className="custom-pbi-setup__embeddedReportContainer custom-pbi-site-reports__embedContainer" style={{ width: "100%", height: "100%" }}>
            <PowerBIEmbed
              embedConfig={embeddedReportConfig}
              cssClassName="report-style-class"
              getEmbeddedComponent={() => {
                // Report component is loaded.
              }}
            />
          </div>
        </div>
      ) : (
        <div className="custom-pbi-site-reports__emptyState">
          {hideDropdown ? "Report could not be loaded for the provided report type." : "Select a report to load it."}
        </div>
      )}
    </div>
  );
};

export default CustomPowerBISiteReports;